import { Component, OnInit, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';

import { PageNames } from 'src/app/configs/page-names.enum';
import { QueryStringTypesEnum } from 'src/app/configs/query-string-types';
import { ApiTokenService } from 'src/app/core/api/services';
import { EventTypes } from 'src/app/core/constants/event-types';
import { AppDomService } from 'src/app/core/services/dom.service';
import { DynamicTabService } from 'src/app/core/services/dynamic-tab.service';
import {
  GetApiTokenByIdDto,
  GetApiTokensWithPaginationDto,
  GetEnumValuesDto,
  OtpStatus,
  PaginatedListOfGetApiTokensWithPaginationDto,
} from '../../../core/api/models';
import { GetRulesByClientDto } from '../../../core/api/models/get-rules-by-client-dto';
import { GetRulesByIpDto } from '../../../core/api/models/get-rules-by-ip-dto';
import { SetIpRateLimitRuleRequest } from '../../../core/api/models/set-ip-rate-limit-rule-request';
import { SplitPipe } from '../../../core/pipes/split.pipe';
import { AppRule } from '../../../core/types/app-rule.type';
import { BaseIndexPageComponent } from '../../shared/base/components/base-index-page.component';
import { BaseIndexModule } from '../../shared/base/modules/base-index.module';
import { FormPageConfig } from './../../../core/models/form-page-config.model';
import { AllIpRulesComponent } from './all-ip-rules/all-ip-rules.component';
import { ApiTokenFormComponent } from './api-token-form/api-token-form.component';
import { ChangeStatusFormComponent } from './change-status-form/change-status-form.component';
import { ClientRulesComponent } from './client-rules/client-rules.component';
import { IpRulesComponent } from './ip-rules/ip-rules.component';

@Component({
  selector: 'app-api-token',
  templateUrl: './api-token.component.html',
  imports: [
    BaseIndexModule,
    IpRulesComponent,
    ClientRulesComponent,
    AllIpRulesComponent,
    SplitPipe,
  ],
})
export class ApiTokenComponent
  extends BaseIndexPageComponent
  implements OnInit
{
  //#region Properties

  pageName = PageNames.apiToken;

  /* binding service endpoints */
  protected currentService = inject(ApiTokenService);
  paginationApiMethodName: string =
    this.currentService.apiTokenGetApiTokensWithPagination.name;
  createGetApiMethodName: string = this.currentService.apiTokenCreateGet.name;
  auditApiMethodName: string =
    this.currentService.apiTokenGetApiTokensAudit.name;
  deleteApiMethodName: string = this.currentService.apiTokenDelete.name;
  /* binding service endpoints */

  modelWithPagination$: Observable<PaginatedListOfGetApiTokensWithPaginationDto>;
  items$: Observable<Array<GetApiTokensWithPaginationDto>>;
  formPageConfig: FormPageConfig<GetApiTokenByIdDto> = {};
  showIpRulesForm: boolean = false;
  showClientRulesForm: boolean = false;
  apiTokenId;
  showAllIpRules: boolean = false;
  OtpStatusEnumValues: GetEnumValuesDto[] = [];
  clientRulesModel: GetRulesByClientDto = {};
  ipRulesModel: GetRulesByIpDto = {};
  allRulesModel: SetIpRateLimitRuleRequest[] = [];
  organizationId: number;
  get OtpStatus() {
    return OtpStatus;
  }

  //#endregion

  constructor(public dynamicTabService: DynamicTabService) {
    super();
  }

  ngOnInit() {
    try {
      super.ngOnInit();
      this.pageConfig.isAddBtnWithLoading = false;
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.setting);
    }
  }

  setPageAfterEnterUrl() {
    super.setPageAfterEnterUrl();
    // وارد میشود چک میکنیم که کدام جدول باید نمایش داده شود url زمانی که کاربر با
    let params = this.routingState.parseQueryString();
    for (const [key, value] of Object.entries(params)) {
      if (key == QueryStringTypesEnum.Rules_key) {
        this.onCreateRules(
          value.toString().split('_')[0] as AppRule,
          value.toString().split('_')[1],
          false
        );
      }
    }
  }

  getEnumsValues() {
    this.getEnumValuesApi('OtpStatus')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((values) => (this.OtpStatusEnumValues = values));
  }

  configTableColumns() {
    this.tableColumns = [
      {
        title: this.localizer.id,
        name: 'id',
        hasSearch: true,
        type: 'number',
        noNumberSeparator: true,
      },
      {
        title: `${this.tableVocabs}ip`,
        name: 'ip',
        hasSearch: true,
        dir: 'ltr',
      },
      { title: `${this.tableVocabs}token`, name: 'token', hasSearch: true },
      { title: this.localizer.userName, name: 'userName', hasSearch: true },
      {
        title: `${this.tableVocabs}status`,
        name: 'status',
        hasSearch: true,
        type: 'enum',
        enumName: 'OtpStatus',
      },
      {
        title: this.localizer.createDate,
        name: 'created',
        hasSearch: true,
        type: 'dateTime',
      },
    ];
  }

  //#region Api

  getIpRules() {
    this.currentService
      .apiTokenGetIpRulesById({ ApiTokenId: this.apiTokenId })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.pageConfig.loading = false))
      )
      .subscribe((item) => {
        try {
          if (item) {
            this.ipRulesModel = item.data;
            this.showIpRulesForm = true;
          }
        } catch (error) {
          this.loggerService.logError(this.tagName, error, EventTypes.setting);
        }
      });
  }

  getClientRules() {
    this.unSubs();
    this.currentService
      .apiTokenGetClientRulesById({ ApiTokenId: this.apiTokenId })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.pageConfig.loading = false))
      )
      .subscribe((item) => {
        if (item) {
          this.showClientRulesForm = true;
          this.clientRulesModel = item?.data;
        }
      });
  }

  onShowAllRules() {
    this.currentService
      .apiTokenGetAllIpRules()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((item) => {
        if (item) {
          this.allRulesModel = item?.data;
          this.showAllIpRules = true;
        }
      });
  }

  //Get ById
  baseGetByIdApi(id: number): void {
    this.unSubs();

    this.pageConfig.loading = true;

    this.formPageConfig.editId = id;

    this.currentService
      .apiTokenGetApiTokenById({ Id: id })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseGetByIdObserverFormModal);
  }

  //#endregion

  onShowChangeStatus(id, status) {
    try {
      this.unSubs();

      let data = {
        title: `${this.formPageConfig?.vocabs}changeStatusTitle`,
        isEdit: true,
        model: {
          id: id,
          status: status,
        },
        currentService: this.currentService,
        methodName: this.currentService.apiTokenUpdateStatus.name,
      };

      const modalResult = this.dialogService.openModal({
        component: ChangeStatusFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.setting);
    }
  }

  onCreateRules(type: AppRule, tokenId, setFiltersAndUrl = true) {
    this.showParent = false;
    this.setParentQueryString();
    if (setFiltersAndUrl) {
      this.location.go(
        window.location.pathname +
          '?' +
          QueryStringTypesEnum.Rules_key +
          '=' +
          type +
          '_' +
          tokenId
      );
    }
    this.unSubs();
    this.apiTokenId = tokenId;
    this.pageConfig.loading = true;

    if (type == 'ipRules') {
      this.getIpRules();
    } else if (type == 'clientRules') {
      this.getClientRules();
    }
  }

  onCreate() {
    this.unSubs();
    let data = {
      title: `${this.formPageConfig?.vocabs}addTitle`,
      isEdit: false,
      organizationId: this.currentUserInfo?.organizationId,
    };

    const modalResult = this.dialogService.openModal({
      component: ApiTokenFormComponent,
      data: data,
    });

    modalResult
      .afterClosed()
      .pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          AppDomService.removeModalScrollClassFromHtml();
        })
      )
      .subscribe((res) => {
        if (res) {
          this.getTableData();
          this.cd.detectChanges();
        }
      });
  }

  openEditFormModal(model: GetApiTokenByIdDto) {
    try {
      this.unSubs();
      let data = {
        title: `${this.formPageConfig?.vocabs}editTitle`,
        model: model,
        organizationId: this.organizationId,
        isEdit: true,
      };

      const modalResult = this.dialogService.openModal({
        component: ApiTokenFormComponent,
        data: data,
      });

      modalResult
        .afterClosed()
        .pipe(
          takeUntil(this.onDestroy$),
          tap(() => {
            AppDomService.removeModalScrollClassFromHtml();
          })
        )
        .subscribe((res) => {
          if (res) {
            this.getTableData();
            this.cd.detectChanges();
          }
        });
    } catch (error) {
      this.loggerService.logError(this.tagName, error, EventTypes.setting);
    }
  }

  getFormResponse($event) {
    this.showIpRulesForm = false;
    this.showClientRulesForm = false;
  }

  onShowSafeIps() {
    this.dynamicTabService.setNewTab('');
    this.dynamicTabService.setNewTab('safeIps');
    this.dynamicTabService.setActiveTab('safeIps');
  }
}
